import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { 
  LayoutDashboard, 
  FileText, 
  Layout, 
  LogOut, 
  ChevronRight, 
  Menu, 
  X 
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface AdminLayoutProps {
  children: React.ReactNode;
  title: string;
  subtitle?: string;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children, title, subtitle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navItems = [
    { path: '/admin', label: 'Dashboard', icon: <LayoutDashboard className="w-5 h-5" /> },
    { path: '/admin/blogs', label: 'Blog Posts', icon: <FileText className="w-5 h-5" /> },
    { path: '/admin/portfolios', label: 'Portfolio', icon: <Layout className="w-5 h-5" /> },
  ];

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/admin/login');
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 flex flex-col md:flex-row">
      {/* Mobile menu button */}
      <button 
        className="md:hidden fixed top-4 right-4 z-50 bg-gray-800 p-2 rounded-md"
        onClick={toggleSidebar}
      >
        {sidebarOpen ? (
          <X className="w-6 h-6 text-gray-200" />
        ) : (
          <Menu className="w-6 h-6 text-gray-200" />
        )}
      </button>

      {/* Sidebar/Navigation */}
      <div 
        className={cn(
          "bg-gray-800 fixed md:static inset-y-0 left-0 z-40 w-64 transform transition-transform duration-300 ease-in-out overflow-y-auto",
          sidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        )}
      >
        <div className="p-4 border-b border-gray-700">
          <Link to="/admin" className="flex items-center">
            <span className="text-xl font-bold text-teal-400">MCubes</span>
            <span className="text-xl font-bold ml-2">Admin</span>
          </Link>
        </div>
        
        <nav className="mt-8 px-4">
          <ul className="space-y-2">
            {navItems.map((item) => (
              <li key={item.path}>
                <Link 
                  to={item.path} 
                  className={cn(
                    "flex items-center px-4 py-3 rounded-md transition-colors",
                    location.pathname === item.path
                      ? "bg-gray-700 text-teal-400"
                      : "text-gray-300 hover:bg-gray-700 hover:text-white"
                  )}
                  onClick={() => setSidebarOpen(false)}
                >
                  {item.icon}
                  <span className="ml-3">{item.label}</span>
                  {location.pathname === item.path && (
                    <ChevronRight className="w-5 h-5 ml-auto" />
                  )}
                </Link>
              </li>
            ))}
          </ul>
          
          <div className="mt-auto pt-8 pb-4">
            <Button 
              variant="ghost" 
              className="w-full justify-start text-gray-300 hover:text-white hover:bg-gray-700"
              onClick={handleSignOut}
            >
              <LogOut className="w-5 h-5 mr-3" />
              Sign Out
            </Button>
          </div>
        </nav>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        <header className="bg-gray-800 border-b border-gray-700 p-6 md:sticky md:top-0 md:z-10">
          <div className="max-w-7xl mx-auto">
            <h1 className="text-2xl font-bold text-white">{title}</h1>
            {subtitle && <p className="mt-1 text-gray-400">{subtitle}</p>}
          </div>
        </header>
        
        <main className="flex-1 p-6">
          <div className="max-w-7xl mx-auto">
            {children}
          </div>
        </main>
        
        <footer className="bg-gray-800 border-t border-gray-700 p-4 text-center text-gray-500 text-sm">
          <p>© {new Date().getFullYear()} MCubes Admin Dashboard</p>
        </footer>
      </div>
    </div>
  );
};

export default AdminLayout; 